import { atom } from "@mongez/react-atom";

/**
 * Complete server release projection consumed by the web application.
 *
 * @example
 * const features: PlatformFeatures = defaultPlatformFeatures;
 */
export type PlatformFeatures = {
  serverUploads: boolean;
  shorts: boolean;
  whatsapp: boolean;
  quizzes: boolean;
  liveSessions: boolean;
  courses: boolean;
  reports: boolean;
  commentsAndRatings: boolean;
  videoAnalytics: boolean;
  socialFeed: boolean;
  gamification: boolean;
  passkeyLogin: boolean;
  qrLogin: boolean;
  studentCards: boolean;
  modernHome: boolean;
  /** Exam-paper view of the quiz screen (release control `examPaperView`). */
  examPaperView: boolean;
  campaigns: boolean;
  studentReports: boolean;
  communityGroups: boolean;
  trainingGround: boolean;
  inquiries: boolean;
  mobileApp: boolean;
  /** League v2 shown on the web channel (server already applies plan + channel). */
  league: boolean;
  /** Claimable tasks board shown on the web channel. */
  tasks: boolean;
};

/**
 * Fail-closed defaults for every platform release consumed by web.
 *
 * @example
 * const gamificationReleased = defaultPlatformFeatures.gamification;
 */
export const defaultPlatformFeatures: PlatformFeatures = {
  serverUploads: false,
  shorts: false,
  whatsapp: false,
  quizzes: false,
  liveSessions: false,
  courses: false,
  reports: false,
  commentsAndRatings: false,
  videoAnalytics: false,
  socialFeed: false,
  gamification: false,
  passkeyLogin: false,
  qrLogin: false,
  studentCards: false,
  modernHome: false,
  examPaperView: false,
  campaigns: false,
  studentReports: false,
  communityGroups: false,
  trainingGround: false,
  inquiries: false,
  mobileApp: false,
  league: false,
  tasks: false,
};

function recordOf(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  return value as Record<string, unknown>;
}

/**
 * Convert an untrusted server feature map into the complete typed web projection.
 *
 * @example
 * const features = projectPlatformFeatures(response.data.features);
 */
export function projectPlatformFeatures(payload: unknown): PlatformFeatures {
  const features = recordOf(payload);
  const projection = { ...defaultPlatformFeatures };

  for (const feature of Object.keys(projection) as Array<
    keyof PlatformFeatures
  >) {
    projection[feature] = features?.[feature] === true;
  }

  // Backends released before League v2 do not send `tasks`: the tasks board
  // then follows gamification exactly as it did before the flag existed.
  if (typeof features?.tasks !== "boolean") {
    projection.tasks = projection.gamification;
  }

  return projection;
}

/**
 * Reactive client state for the current request's complete release projection.
 *
 * @example
 * const features = platformFeaturesAtom.useValue();
 */
export const platformFeaturesAtom = atom<PlatformFeatures>({
  key: "platformFeatures",
  default: { ...defaultPlatformFeatures },
});

/**
 * Reset request-global feature state before applying one complete projection.
 *
 * @example
 * resetPlatformFeatures(loaderData.platformFeatures);
 */
export function resetPlatformFeatures(
  platformFeatures: PlatformFeatures = defaultPlatformFeatures,
) {
  platformFeaturesAtom.reset();
  platformFeaturesAtom.update({ ...platformFeatures });
}

/**
 * Extract the tenant's public numeric client id from the `/platform-features`
 * payload. Anything that is not a positive safe integer fails closed to null.
 *
 * @example
 * const clientId = projectPlatformClientId(response.data.clientId); // 12 | null
 */
export function projectPlatformClientId(value: unknown): number | null {
  const clientId =
    typeof value === "string" && /^[1-9]\d*$/.test(value)
      ? Number(value)
      : value;

  return typeof clientId === "number" &&
    Number.isSafeInteger(clientId) &&
    clientId > 0
    ? clientId
    : null;
}

/**
 * Public numeric id of the tenant serving the current request (null when the
 * backend did not report one).
 *
 * @example
 * const clientId = platformClientIdAtom.useValue();
 */
export const platformClientIdAtom = atom<number | null>({
  key: "platformClientId",
  default: null,
});
